// mention-highlight.js — pure @mention HTML highlighter (TPT33). Mirrors the boundary/
// matching rules of api/src/lib/mentions.js's parseMentions() so "highlighted in the
// UI" and "notified server-side" stay in sync. Zero imports — keeps this module leaf-
// testable with a plain static import, no stub-then-dynamic-import dance (compare
// lib/tag-typeahead.test.js, whose subject pulls in utils.js -> api.js -> localStorage
// at module scope).
//
// Deliberately diverges from the Task App's _renderCommentContent
// (ai/todo/server/src/client/task-board.js): no bare-handle fallback (@nobody never
// highlights — the server has no fallback either, so highlighting it would promise a
// notification that never fires), and no claude/codex/pi pseudo-handles (the web app
// has no such users).

// Shared with lib/mention-typeahead.js — the SAME leading-boundary class
// api/src/lib/mentions.js's parseMentions() uses, so "would the typeahead trigger
// here" and "will the server resolve this token" never disagree.
export const MENTION_BOUNDARY_BEFORE = '[^\\w.@-]';

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// marked@18's own text-token escaper (api/node_modules/marked/lib/marked.esm.js,
// escapeTestNoEncode/escapeReplaceNoEncode) — verified byte-for-byte against the
// installed source. renderMarkdown() runs raw comment/description text through this
// before we ever see the HTML, so a handle containing & < > " ' must be escaped the
// same way before it can match inside the rendered string — e.g. member "O'Brien"
// appears in the HTML as "O&#39;Brien", not "O'Brien".
const MARKED_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const MARKED_ESCAPE_RE = /[<>"']|&(?!(#\d{1,7}|#[Xx][a-fA-F0-9]{1,6}|\w+);)/g;
export function escapeLikeMarked(s) {
  return String(s).replace(MARKED_ESCAPE_RE, (ch) => MARKED_ESCAPES[ch]);
}

// mentionHandles(members) -> string[], longest-first (so "@John Smith" wins over the
// shorter "@John" it contains, same alternation-ordering trick parseMentions() uses),
// deduped case-insensitively. Each accepted member (user_id != null) contributes its
// trimmed name AND its email local-part as independent handles — mirrors
// api/src/lib/mentions.js's own candidate list exactly, so every handle this
// highlighter lights up is one the server would also resolve.
export function mentionHandles(members) {
  const seen = new Set();
  const handles = [];
  for (const m of members || []) {
    if (!m || m.user_id == null) continue;
    const candidates = [];
    if (m.name && String(m.name).trim()) candidates.push(String(m.name).trim());
    if (m.email) {
      const local = String(m.email).split('@')[0];
      if (local) candidates.push(local);
    }
    for (const h of candidates) {
      const key = h.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      handles.push(h);
    }
  }
  return handles.sort((a, b) => b.length - a.length);
}

export function buildMentionRegex(handles) {
  if (!handles || !handles.length) return null;
  const alternation = handles.map((h) => escapeRegExp(escapeLikeMarked(h))).join('|');
  return new RegExp(`(^|${MENTION_BOUNDARY_BEFORE})@(${alternation})(?![\\w-])`, 'gi');
}

// highlightMentions(html, members) — wraps matched @handle tokens in
// <span class="mention">, operating ONLY on text between tags: `<[^>]*>` chunks are
// skipped wholesale, so `<a href="mailto:x@y">` is never touched (its only failure
// mode — a `>` inside a raw hand-written attribute value — is a pre-existing
// renderMarkdown()-has-no-sanitizer hazard this doesn't widen; comment bodies no longer
// hit it, since renderSafeMarkdown() escapes all raw HTML and every attribute value).
// Deliberately does NOT skip <code>/<pre>/<a> bodies: the server scans raw text with
// no code-fence exception, so skipping code here would break "highlighted <=>
// notified" in the other direction. NOT idempotent — only ever call this on fresh
// renderMarkdown() / renderSafeMarkdown() output, never on its own prior output (would
// double-wrap).
export function highlightMentions(html, members) {
  const source = String(html || '');
  if (!source) return '';
  const re = buildMentionRegex(mentionHandles(members));
  if (!re) return source;
  return source.replace(/<[^>]*>|([^<]+)/g, (chunk, text) => {
    if (text == null) return chunk; // a tag — untouched
    return text.replace(re, (_m, before, handle) => `${before}<span class="mention">@${handle}</span>`);
  });
}
